import { useEffect, useRef, useState } from 'react';
import { useAuthContext, useTenantContext } from './contexts.tsx';
import { resolveApiBaseUrl } from './apiFetch.ts';

/**
 * One live tag value-change pushed over the SCADA Server-Sent Events stream
 * (`GET /api/v1/scada/live-tags/stream`). Mirrors the backend wire payload:
 * `{ tagId, tagPath, value, quality, ts }`.
 */
export interface LiveTagStreamUpdate {
  /** Tag id — null for ad-hoc (unbound) writes the gateway forwards by path. */
  tagId: string | null;
  /** Fully-qualified tag path. */
  tagPath: string;
  /** Latest value (numeric or string depending on the tag). */
  value: number | string | null;
  /** OPC quality label ("Good" | "Uncertain" | "Bad" | …). */
  quality?: string | null;
  /** Server timestamp of the sample (ISO-8601). */
  ts: string;
}

/** Connection lifecycle of the SSE stream, mapped to the shared live vocabulary. */
export type LiveTagStreamState = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface UseLiveTagStreamOptions {
  /** Disable the stream (e.g. before a tenant/token is available). Default true. */
  enabled?: boolean;
  /**
   * Endpoint path of the SSE stream. Defaults to the SCADA live-tag stream.
   * Exposed for tests / alternate streams; callers normally omit it.
   */
  path?: string;
  /**
   * Upper bound on retained map entries (LRU). On always-on operator screens
   * that cycle through many distinct tags/ad-hoc paths the latest-value map
   * would otherwise grow without bound. When the cap is exceeded the least
   * recently updated entries are evicted. Default 5000; set to 0/undefined to
   * disable the cap (not recommended for long-running dashboards).
   */
  maxEntries?: number;
}

const DEFAULT_MAX_ENTRIES = 5000;

export interface UseLiveTagStreamResult {
  /**
   * Latest update per key. Each update is stored under BOTH its `tagId`
   * (when present) and its `tagPath`, so callers can look up by whichever
   * identifier their list rows carry.
   */
  updates: Map<string, LiveTagStreamUpdate>;
  /** Current connection state. */
  state: LiveTagStreamState;
  /** Monotonic counter bumped on every received frame — handy as a render tick. */
  frameCount: number;
}

const DEFAULT_PATH = '/api/v1/scada/live-tags/stream';
const RECONNECT_DELAY_MS = 3000;

/**
 * Subscribe to the SCADA live-tag Server-Sent Events stream and surface the
 * latest value per tag as a push feed — no polling.
 *
 * `EventSource` cannot attach the `Authorization` / `X-Tenant-Id` headers the
 * gateway requires, so this hook drives the stream with `fetch` + a streaming
 * `ReadableStream` reader instead (the same authenticated-fetch shape the rest
 * of `@tyndo/hooks` uses). It parses the `event: tag-value` / `data: {…}`
 * blocks, ignores heartbeat comment lines, and auto-reconnects after a brief
 * delay when the connection drops.
 *
 * Views overlay `updates` on top of their initial snapshot so tiles update
 * live without re-fetching; the stream replaces the polling loop.
 */
export function useLiveTagStream(
  options: UseLiveTagStreamOptions = {},
): UseLiveTagStreamResult {
  const { enabled = true, path = DEFAULT_PATH, maxEntries = DEFAULT_MAX_ENTRIES } = options;
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();

  const [updates, setUpdates] = useState<Map<string, LiveTagStreamUpdate>>(
    () => new Map(),
  );
  const [state, setState] = useState<LiveTagStreamState>('connecting');
  const [frameCount, setFrameCount] = useState(0);

  // Hold the live map in a ref so a streamed frame mutates a single instance
  // and we publish a fresh Map reference per frame without re-subscribing.
  const mapRef = useRef<Map<string, LiveTagStreamUpdate>>(new Map());

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setState('offline');
      return;
    }

    let cancelled = false;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();

    const baseUrl = resolveApiBaseUrl();
    const normalized = path.replace(/^\/api\/v\d+/, '');
    const url = path.startsWith('http') ? path : `${baseUrl}${normalized}`;

    const applyFrame = (raw: string) => {
      // A frame may carry an `event:` line and a `data:` line. We only act on
      // tag-value data payloads; comment lines (": heartbeat") are skipped.
      const dataLine = raw
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.startsWith('data:'));
      if (!dataLine) return;
      const json = dataLine.slice('data:'.length).trim();
      if (!json) return;
      try {
        const parsed = JSON.parse(json) as LiveTagStreamUpdate;
        if (!parsed || (!parsed.tagId && !parsed.tagPath)) return;
        const map = mapRef.current;
        // Map preserves insertion order; delete-then-set moves the key to the
        // most-recently-used position so the eviction sweep drops cold keys.
        const touch = (key: string) => {
          if (map.has(key)) map.delete(key);
          map.set(key, parsed);
        };
        if (parsed.tagId) touch(parsed.tagId);
        if (parsed.tagPath) touch(parsed.tagPath);
        // LRU eviction: trim from the front (oldest) until within the cap.
        if (maxEntries && maxEntries > 0) {
          while (map.size > maxEntries) {
            const oldest = map.keys().next().value;
            if (oldest === undefined) break;
            map.delete(oldest);
          }
        }
        if (!cancelled) {
          // Publish a fresh Map reference so dependent memos recompute.
          setUpdates(new Map(mapRef.current));
          setFrameCount((n) => n + 1);
        }
      } catch {
        // Malformed frame — drop it; the next frame recovers.
      }
    };

    const connect = async () => {
      setState((prev) => (prev === 'live' ? 'reconnecting' : 'connecting'));
      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantId) headers['X-Tenant-Id'] = tenantId;

        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`stream ${response.status}`);
        }

        setState('live');
        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Read until the stream closes or the effect is torn down.
        // SSE frames are separated by a blank line (\n\n).
        for (;;) {
          const { value, done } = await reader.read();
          if (done || cancelled) break;
          buffer += decoder.decode(value, { stream: true });
          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            applyFrame(frame);
            sep = buffer.indexOf('\n\n');
          }
        }
        if (!cancelled) scheduleReconnect();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        scheduleReconnect();
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      setState('reconnecting');
      reconnectTimer = setTimeout(() => {
        if (!cancelled) void connect();
      }, RECONNECT_DELAY_MS);
    };

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        controller.abort();
      } catch {
        /* ignore */
      }
      // `reader.cancel()` REJECTS once controller.abort() has torn the body
      // down — "AbortError: BodyStreamBuffer was aborted". `void` discards the
      // value but attaches no rejection handler, and the surrounding try/catch
      // only catches a SYNCHRONOUS throw, so that rejection escaped to
      // window.unhandledrejection and the console reported correct
      // abort-on-unmount as an application error on every navigation away.
      //
      // Swallow AbortError HERE ONLY — on the teardown path, where an abort is
      // the expected outcome. Anything else is a real teardown fault and is
      // still reported.
      reader?.cancel().catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof TypeError) return; // already-released reader lock
        throw err;
      });
      setState('offline');
    };
  }, [enabled, path, token, tenantId, maxEntries]);

  return { updates, state, frameCount };
}
